import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useToast } from '../components/Toast.jsx';
import { listProjects, createProject, deleteProject } from '../api/projects.js';
import { createPortalSession } from '../api/billing.js';
import TierBadge from '../components/TierBadge.jsx';
import LoadingSpinner from '../components/LoadingSpinner.jsx';

const STATUS_STYLES = {
  draft: 'bg-gray-700 text-gray-300',
  ready: 'bg-blue-900/50 text-blue-300',
  rendering: 'bg-yellow-900/50 text-yellow-300',
  completed: 'bg-green-900/50 text-green-300',
  archived: 'bg-gray-800 text-gray-500',
};

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function DashboardPage() {
  const { user, subscription, fetchUsage } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  async function loadProjects() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listProjects();
      setProjects(data.items || []);
    } catch (err) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCreateProject(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setIsCreating(true);
    try {
      const project = await createProject(newName.trim());
      showToast('Project created', 'success');
      setShowNewForm(false);
      setNewName('');
      navigate(`/projects/${project.id}`);
    } catch (err) {
      showToast(err.message || 'Failed to create project', 'error');
    } finally {
      setIsCreating(false);
    }
  }

  async function handleDelete(projectId, projectName) {
    try {
      await deleteProject(projectId);
      setProjects((prev) => prev.filter((p) => p.id !== projectId));
      showToast(`"${projectName}" deleted`, 'success');
    } catch (err) {
      showToast(err.message || 'Failed to delete project', 'error');
    } finally {
      setDeleteId(null);
    }
  }

  async function handleManageSubscription() {
    setPortalLoading(true);
    try {
      const { portal_url } = await createPortalSession();
      window.location.href = portal_url;
    } catch (err) {
      showToast(err.message || 'Failed to open billing portal', 'error');
      setPortalLoading(false);
    }
  }

  if (isLoading) {
    return <LoadingSpinner message="Loading dashboard..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4">
        <p className="text-red-400">{error}</p>
        <button onClick={loadProjects} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm">
          Retry
        </button>
      </div>
    );
  }

  const tier = subscription?.tier || 'free';
  const hasSubscription = subscription !== null;

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Dashboard</h1>
          <div className="flex items-center gap-3 mt-2">
            <TierBadge tier={tier} />
            {hasSubscription ? (
              <span className="text-sm text-gray-400">
                {subscription.credits_remaining} / {subscription.credits_total} credits
                {subscription.period_end && (
                  <> &middot; resets {formatDate(subscription.period_end)}</>
                )}
              </span>
            ) : (
              <span className="text-sm text-gray-500">Free tier</span>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          {hasSubscription ? (
            <button
              onClick={handleManageSubscription}
              disabled={portalLoading}
              style={{ padding: '0.5rem 1rem', border: '1px solid #4b5563', color: '#d1d5db', background: 'transparent', borderRadius: '0.5rem', fontSize: '0.875rem', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#6b7280'; e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#4b5563'; e.currentTarget.style.color = '#d1d5db'; }}
            >
              {portalLoading ? 'Opening...' : 'Manage Subscription'}
            </button>
          ) : (
            <Link
              to="/pricing"
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
            >
              Subscribe
            </Link>
          )}
        </div>
      </div>

      {/* New Project */}
      <div className="mb-6">
        {showNewForm ? (
          <form onSubmit={handleCreateProject} className="flex flex-col sm:flex-row gap-3 sm:items-center">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Project name"
              maxLength={255}
              autoFocus
              className="flex-1 px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
            />
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={isCreating || !newName.trim()}
                className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm disabled:opacity-50"
              >
                {isCreating ? 'Creating...' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => { setShowNewForm(false); setNewName(''); }}
                style={{ padding: '0.5rem 1rem', color: '#9ca3af', background: 'transparent', border: 'none', fontSize: '0.875rem', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.color = '#fff'; }}
                onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            onClick={() => setShowNewForm(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
          >
            + New Project
          </button>
        )}
      </div>

      {/* Project List */}
      {projects.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-gray-700 rounded-lg">
          <p className="text-gray-400 mb-4">No projects yet</p>
          <button
            onClick={() => setShowNewForm(true)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm"
          >
            Create your first project
          </button>
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <div
              key={project.id}
              style={{
                background: deleteId === project.id ? '#1c1317' : '#111827',
                border: deleteId === project.id ? '1px solid #7f1d1d' : '1px solid #1f2937',
                borderRadius: '0.5rem',
                padding: '1rem',
                position: 'relative',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => { if (deleteId !== project.id) e.currentTarget.style.borderColor = '#374151'; }}
              onMouseLeave={e => { if (deleteId !== project.id) e.currentTarget.style.borderColor = '#1f2937'; }}
            >
              {deleteId === project.id ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', padding: '0.5rem 0' }}>
                  <p style={{ color: '#f87171', fontSize: '0.875rem' }}>
                    Delete &ldquo;{project.name}&rdquo;?
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleDelete(project.id, project.name)}
                      style={{ padding: '0.375rem 0.75rem', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#ef4444'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#dc2626'; }}
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setDeleteId(null)}
                      style={{ padding: '0.375rem 0.75rem', background: 'transparent', color: '#9ca3af', border: '1px solid #374151', borderRadius: '0.375rem', fontSize: '0.75rem', cursor: 'pointer' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#6b7280'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.borderColor = '#374151'; }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <Link to={`/projects/${project.id}`} style={{ display: 'block', textDecoration: 'none' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <h3 style={{ color: '#fff', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '0.5rem' }}>{project.name}</h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLES[project.status] || STATUS_STYLES.draft}`}>
                        {project.status}
                      </span>
                    </div>
                  </Link>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: 0 }}>
                      Updated {formatDate(project.updated_at)}
                    </p>
                    <button
                      onClick={(e) => { e.preventDefault(); setDeleteId(project.id); }}
                      title="Delete project"
                      style={{ color: '#4b5563', background: 'transparent', border: 'none', cursor: 'pointer', padding: '0.25rem' }}
                      onMouseEnter={e => { e.currentTarget.style.color = '#f87171'; }}
                      onMouseLeave={e => { e.currentTarget.style.color = '#4b5563'; }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
